<template>
  <div class="page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>会员套餐</span>
        <span class="hint">积分按「月基数」填写，年付实际发放 = 月基数 × 12；存储留空 = 该档无上限，未开通会员固定 20GB</span>
      </div>
      <el-table v-loading="loading" :data="list" border stripe>
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column label="档位" width="90">
          <template slot-scope="{ row }">{{ tierLabel(row.tier) }}</template>
        </el-table-column>
        <el-table-column label="周期" width="80">
          <template slot-scope="{ row }">{{ cycleLabel(row.cycle) }}</template>
        </el-table-column>
        <el-table-column prop="name" label="名称" min-width="100" />
        <el-table-column prop="price" label="价格(元)" width="100" />
        <el-table-column prop="credits" label="月积分" width="90" />
        <el-table-column label="年付积分" width="100">
          <template slot-scope="{ row }">
            {{ String(row.cycle || '').toLowerCase() === 'yearly' ? (Number(row.credits) || 0) * 12 : (Number(row.credits) || 0) }}
          </template>
        </el-table-column>
        <el-table-column prop="seats" label="协作人数" width="90" />
        <el-table-column label="存储" width="110">
          <template slot-scope="{ row }">{{ formatStorage(row.storageLimitGb) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="80">
          <template slot-scope="{ row }">{{ labelOf(COMMON_STATUS, row.status) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDialog(row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog :title="form.id ? '编辑会员套餐' : '新增会员套餐'" :visible.sync="visible" width="520px">
      <el-form ref="form" :model="form" :rules="rules" label-width="96px">
        <el-form-item label="档位" prop="tier">
          <el-select v-model="form.tier" :disabled="!!form.id" style="width: 100%">
            <el-option v-for="opt in tierOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="周期" prop="cycle">
          <el-select v-model="form.cycle" :disabled="!!form.id" style="width: 100%">
            <el-option v-for="opt in cycleOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="名称" prop="name">
          <el-input v-model="form.name" />
        </el-form-item>
        <el-form-item label="价格(元)" prop="price">
          <el-input-number v-model="form.price" :min="0" :precision="2" style="width: 100%" />
        </el-form-item>
        <el-form-item label="月积分" prop="credits">
          <el-input-number v-model="form.credits" :min="1" style="width: 100%" />
        </el-form-item>
        <el-form-item label="协作人数" prop="seats">
          <el-input-number v-model="form.seats" :min="1" style="width: 100%" />
        </el-form-item>
        <el-form-item label="存储上限">
          <el-input-number v-model="form.storageLimitGb" :min="1" :precision="0" style="width: 100%" />
          <div class="hint">单位 GB（1TB = 1024GB）。基础 3072、进阶 10240、专业 51200、旗舰 133120、至尊 307200；清空表示该档无上限</div>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="form.status" style="width: 100%">
            <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getMembershipPlans, saveMembershipPlan } from '@/api/admin'
import { COMMON_STATUS, labelOf, optionsOf } from '@/utils/adminConstants'

const TIER_LABEL = {
  basic: '基础版',
  plus: '进阶版',
  pro: '专业版',
  flagship: '旗舰版',
  supreme: '至尊版'
}

export default {
  name: 'AdminMembership',
  data() {
    return {
      COMMON_STATUS,
      loading: false,
      list: [],
      visible: false,
      submitting: false,
      tierOptions: [
        { value: 'basic', label: '基础版' },
        { value: 'plus', label: '进阶版' },
        { value: 'pro', label: '专业版' },
        { value: 'flagship', label: '旗舰版' },
        { value: 'supreme', label: '至尊版' }
      ],
      cycleOptions: [
        { value: 'monthly', label: '月付' },
        { value: 'yearly', label: '年付' }
      ],
      form: { id: null, tier: 'basic', cycle: 'monthly', name: '', price: 0, credits: 810, seats: 1, storageLimitGb: null, status: 1 },
      rules: {
        tier: [{ required: true, message: '请选择档位', trigger: 'change' }],
        cycle: [{ required: true, message: '请选择周期', trigger: 'change' }],
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        price: [{ required: true, message: '请输入价格', trigger: 'blur' }],
        credits: [{ required: true, message: '请输入月积分', trigger: 'blur' }],
        seats: [{ required: true, message: '请输入协作人数', trigger: 'blur' }]
      }
    }
  },
  computed: {
    statusOptions() {
      return optionsOf(COMMON_STATUS)
    }
  },
  created() {
    this.loadPlans()
  },
  methods: {
    labelOf,
    tierLabel(tier) {
      return TIER_LABEL[String(tier || '').toLowerCase()] || tier || '-'
    },
    cycleLabel(cycle) {
      return String(cycle || '').toLowerCase() === 'yearly' ? '年付' : '月付'
    },
    formatStorage(gb) {
      const value = Number(gb)
      if (!value || value <= 0) return '无上限'
      if (value % 1024 === 0) return (value / 1024) + ' TB'
      if (value >= 1024) {
        const tb = value / 1024
        const text = Number.isInteger(tb) ? String(tb) : tb.toFixed(1).replace(/\.0$/, '')
        return text + ' TB'
      }
      return value + ' GB'
    },
    async loadPlans() {
      this.loading = true
      try {
        this.list = await getMembershipPlans() || []
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    openDialog(row) {
      this.form = row
        ? { ...row }
        : { id: null, tier: 'basic', cycle: 'monthly', name: '', price: 0, credits: 810, seats: 1, storageLimitGb: null, status: 1 }
      this.visible = true
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          await saveMembershipPlan(this.form)
          this.$message.success('保存成功')
          this.visible = false
          this.loadPlans()
        } catch (e) {
          this.$message.error((e && e.message) || '保存失败')
        } finally {
          this.submitting = false
        }
      })
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.hint {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}
</style>
